import { createApi, retry } from '@reduxjs/toolkit/query/react';
import BackendApiClient from './BackendApiClient';

/**
 * Cache tags, declared once so `providesTags`, `invalidatesTags` and the imperative
 * `util.invalidateTags` calls outside React can never drift into a silent no-op typo.
 */
export const ApiTag = {
  Printers: 'Printers',
};

/**
 * BackendApiClient already attaches the Bearer token and the locale header, and handles
 * 401 -> logout. It also never rejects — it resolves `{ data, status, success }` — so all
 * this has to do is translate that envelope into the `{ data } | { error }` shape RTK
 * Query expects a baseQuery to return (it must return errors, never throw them).
 *
 * Endpoints must return a request config object (`{ method, url, data }`), not the string
 * shorthand — the shorthand would spread into character keys here.
 */
export const backendBaseQuery = async ({ formData = false, ...configuration }) => {
  const { data, status, success } = await BackendApiClient.requestAuthorized(configuration, formData);

  if (success) {
    return { data };
  }

  // Only a dropped connection or a server fault is worth another attempt. A 4xx fails the
  // same way every time, and BackendApiClient has already fired the 401 -> logout hop, so
  // retrying one would trigger it repeatedly.
  return status && status < 500 ? retry.fail({ status, data }) : { error: { status, data } };
};

export default createApi({
  reducerPath: 'api',
  // Off by default so a non-idempotent write is never replayed; reads opt in per endpoint
  // via `extraOptions.maxRetries`.
  baseQuery: retry(backendBaseQuery, { maxRetries: 0 }),
  tagTypes: Object.values(ApiTag),
  endpoints: () => ({}),
});
